import React, { useState } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Popconfirm, Space, Table, Tabs, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import {
  useDeleteSubscriptionPlanMutation,
  useGetLessonDebtsQuery,
  useGetSubscriptionPlansQuery,
  useGetSubscriptionPurchasesQuery,
  useReopenLessonDebtMutation,
  useSettleLessonDebtMutation,
} from 'features/adminOperations/api';
import { LessonDebt, SubscriptionPlan, SubscriptionPurchase } from 'features/adminOperations/types';

import { PlanModal } from './components/PlanModal';
import { PurchaseModal } from './components/PurchaseModal';

function getRemainingColor(item: SubscriptionPurchase, remaining: number) {
  if (item.expired) {
    return 'default';
  }

  return remaining <= 1 ? 'red' : 'green';
}

export default function Subscriptions() {
  const { t } = useTranslation();
  const [showSettled, setShowSettled] = useState(false);
  const { data: plans = [], isFetching: plansLoading, isError: plansError } = useGetSubscriptionPlansQuery();
  const { data: purchases = [], isFetching: purchasesLoading, isError: purchasesError } = useGetSubscriptionPurchasesQuery();
  const { data: debts = [], isFetching: debtsLoading, isError: debtsError } = useGetLessonDebtsQuery(showSettled);
  const [deletePlan, { isLoading: deleting }] = useDeleteSubscriptionPlanMutation();
  const [settleDebt, { isLoading: settling }] = useSettleLessonDebtMutation();
  const [reopenDebt, { isLoading: reopening }] = useReopenLessonDebtMutation();
  const [editing, setEditing] = useState<SubscriptionPlan | 'new' | null>(null);
  const [issuing, setIssuing] = useState(false);

  const handleDelete = async (id: number) => {
    try {
      await deletePlan(id).unwrap();
      message.success(t('admin-extensions.saved'));
    } catch {
      message.error(t('admin-extensions.save-error'));
    }
  };

  const handleSettle = async (id: number) => {
    try {
      await settleDebt(id).unwrap();
      message.success(t('admin-extensions.debt-settled'));
    } catch {
      message.error(t('admin-extensions.save-error'));
    }
  };

  const handleReopen = async (id: number) => {
    try {
      await reopenDebt(id).unwrap();
      message.success(t('admin-extensions.debt-reopened'));
    } catch {
      message.error(t('admin-extensions.save-error'));
    }
  };

  return (
    <>
      {editing !== null && <PlanModal plan={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
      {issuing && <PurchaseModal plans={plans} onClose={() => setIssuing(false)} />}
      <Tabs items={[
        {
          key: 'purchases', label: t('admin-extensions.customers-subscriptions'), children: (
            <>
              {purchasesError && <Alert type="error" showIcon message={t('admin-extensions.load-error')} />}
              <Button type="primary" icon={<PlusOutlined />} disabled={plans.length === 0} onClick={() => setIssuing(true)} style={{ marginBottom: 16 }}>{t('admin-extensions.issue-subscription')}</Button>
              <Table<SubscriptionPurchase> rowKey="id" size="small" loading={purchasesLoading} dataSource={purchases} scroll={{ x: 700 }}>
                <Table.Column title={t('admin-extensions.client')} dataIndex="user_name" />
                <Table.Column title={t('admin-extensions.plan')} dataIndex="name" />
                <Table.Column title={t('admin-extensions.remaining')} dataIndex="available_lessons" render={(value: number, item: SubscriptionPurchase) => <Tag color={getRemainingColor(item, value)}>{value} / {item.lessons_total}</Tag>} />
                <Table.Column title={t('admin-extensions.purchase-date')} dataIndex="purchased_on" render={(value: string) => dayjs(value).format('DD.MM.YYYY')} />
                <Table.Column title={t('admin-extensions.expires')} dataIndex="expires_on" render={(value: string | null) => value ? dayjs(value).format('DD.MM.YYYY') : t('admin-extensions.no-expiry')} />
              </Table>
            </>
          )
        },
        {
          key: 'plans', label: t('admin-extensions.plans'), children: (
            <>
              {plansError && <Alert type="error" showIcon message={t('admin-extensions.load-error')} />}
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing('new')} style={{ marginBottom: 16 }}>{t('admin-extensions.create-plan')}</Button>
              <Table<SubscriptionPlan> rowKey="id" size="small" loading={plansLoading} dataSource={plans} scroll={{ x: 600 }}>
                <Table.Column title={t('admin-extensions.name')} dataIndex="name" />
                <Table.Column title={t('admin-extensions.lessons')} dataIndex="lessons" />
                <Table.Column title={t('admin-extensions.price')} dataIndex="price" render={(value: string) => `${value} ₽`} />
                <Table.Column title={t('admin-extensions.expires')} dataIndex="expires_at_month_end" render={(value: boolean) => t(value ? 'admin-extensions.end-of-month' : 'admin-extensions.no-expiry')} />
                <Table.Column
                  render={(_, item: SubscriptionPlan) => (
                    <Space>
                      <Button onClick={() => setEditing(item)}>{t('admin-extensions.edit')}</Button>
                      <Popconfirm
                        title={t('admin-extensions.delete-plan-confirm')}
                        okText={t('yes')}
                        cancelText={t('no')}
                        onConfirm={() => {
                          void handleDelete(item.id);
                        }}
                      >
                        <Button danger loading={deleting}>{t('admin-extensions.delete')}</Button>
                      </Popconfirm>
                    </Space>
                  )}
                />
              </Table>
            </>
          )
        },
        {
          key: 'debts', label: `${t('admin-extensions.debts')} (${debts.filter((debt) => debt.kind === 'debt').length})`, children: (
            <>
              {debtsError && <Alert type="error" showIcon message={t('admin-extensions.load-error')} />}
              <Checkbox checked={showSettled} onChange={(event) => setShowSettled(event.target.checked)} style={{ marginBottom: 16 }}>
                {t('admin-extensions.show-settled')}
              </Checkbox>
              <Table<LessonDebt> rowKey="id" size="small" loading={debtsLoading} dataSource={debts} scroll={{ x: 600 }}>
                <Table.Column title={t('admin-extensions.client')} dataIndex="user_name" />
                <Table.Column title={t('admin-extensions.date')} dataIndex="start_time" render={(value: string) => dayjs(value).format('DD.MM.YYYY HH:mm')} />
                <Table.Column title={t('admin-extensions.booking')} dataIndex="booking_id" />
                <Table.Column title={t('admin-extensions.status')} dataIndex="kind" render={(value: string) => t(value === 'settled' ? 'admin-extensions.paid' : 'admin-extensions.unpaid')} />
                <Table.Column
                  render={(_, item: LessonDebt) => (
                    <Popconfirm
                      title={t(item.kind === 'settled' ? 'admin-extensions.reopen-confirm' : 'admin-extensions.settle-confirm')}
                      okText={t('yes')}
                      cancelText={t('no')}
                      onConfirm={() => {
                        if (item.kind === 'settled') {
                          void handleReopen(item.id);
                        } else {
                          void handleSettle(item.id);
                        }
                      }}
                    >
                      <Button loading={settling || reopening}>
                        {t(item.kind === 'settled' ? 'admin-extensions.reopen' : 'admin-extensions.settle')}
                      </Button>
                    </Popconfirm>
                  )}
                />
              </Table>
            </>
          )
        },
      ]}
      />
    </>
  );
}
